import {
  Checkbox,
  RadioGroup,
  Text,
  TextHighlight,
  TextSize,
  TextWeight
} from '@focaldata/cin-ui-components'
import {
  KpiBase,
  KpiMetricType
} from '../../../data/gql-gen/questionnaire/graphql'
import useStyles from './KpiSettingsDialog.styles'
import { KpiSubjectKind } from './kpi.utils'
import {
  KpiDialogOption,
  KpiMeasureDraft,
  setKpiDraftBase,
  toggleKpiNetTick,
  toggleKpiTick
} from './kpiDialogModel'

export const METRIC_HEADING = 'What number should we track?'

const NET_SIDES = [
  { side: 'positive', heading: 'Positive' },
  { side: 'negative', heading: 'Negative' }
] as const

export const getMetricName = (
  metricType: KpiMetricType,
  isOneOption: boolean
) => {
  switch (metricType) {
    case KpiMetricType.Net:
      return 'Net: % positive minus % negative'
    case KpiMetricType.RankedTop:
      return isOneOption
        ? '% ranking this option first'
        : '% ranking any of the ticked options first'
    case KpiMetricType.Average:
      return 'Average of the answers'
    case KpiMetricType.Share:
      return isOneOption
        ? '% choosing this option'
        : '% choosing any of the ticked options'
  }
}

const getTickHint = (subjectKind: KpiSubjectKind, draft: KpiMeasureDraft) => {
  if (draft.metricType === KpiMetricType.Net) {
    return 'Ticking an option on one side clears it on the other. Options in neither set still count in the total.'
  }
  switch (subjectKind) {
    case 'multiSelect':
      return 'Each person counts once, even if they tick more than one.'
    case 'matrixMulti':
      return 'Each person counts once, even if they tick more than one of these in the row.'
    case 'ranked':
      return 'Tick more than one to count anyone who puts any of them in first place.'
    default: {
      const { ticks, suggested } = draft
      const isSuggestion =
        suggested.length > 0 &&
        ticks.length === suggested.length &&
        ticks.every((id) => suggested.includes(id))
      return isSuggestion
        ? 'Suggested from the option wording. Check before saving.'
        : null
    }
  }
}

export const getKpiFootnote = (
  {
    subjectKind,
    hasDontKnow
  }: { subjectKind: KpiSubjectKind; hasDontKnow: boolean },
  hasEarlierWaves: boolean,
  kpiNoun = 'KPI'
) => {
  if (subjectKind === 'slider') {
    return hasEarlierWaves
      ? '% scoring above a value on a slider is not available yet. Applies from this wave; earlier waves keep their own KPI.'
      : '% scoring above a value on a slider is not available yet. Applies from this wave.'
  }
  const earlierWaves = hasEarlierWaves
    ? ` Earlier waves keep their own ${kpiNoun}.`
    : ''
  const dontKnow = hasDontKnow
    ? ' "Don\'t know" answers count in the total.'
    : ''
  return `Applies from this wave.${earlierWaves}${dontKnow}`
}

interface TickFieldsProps<D extends KpiMeasureDraft> {
  subjectKind: KpiSubjectKind
  options: KpiDialogOption[]
  draft: D
  heading: string
  onChange: (change: (current: D) => D) => void
}

/** The options a Share or Net counts: one list, or a Positive and a Negative side. */
export const KpiTickFields = <D extends KpiMeasureDraft>({
  subjectKind,
  options,
  draft,
  heading,
  onChange
}: TickFieldsProps<D>) => {
  const { classes } = useStyles()
  const tickHint = getTickHint(subjectKind, draft)

  return (
    <div className={classes.section}>
      {draft.metricType === KpiMetricType.Net ? (
        <div className={classes.netColumns}>
          {NET_SIDES.map(({ side, heading: sideHeading }) => (
            <div
              key={side}
              role="group"
              aria-label={sideHeading}
              className={classes.tickGroup}
            >
              <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
                {sideHeading}
              </Text>
              {options.map((option) => (
                <Checkbox
                  key={option.id}
                  label={option.label}
                  ariaLabel={`${sideHeading}: ${option.label}`}
                  checked={draft[side].includes(option.id)}
                  onChange={() =>
                    onChange((current) =>
                      toggleKpiNetTick(current, side, option.id)
                    )
                  }
                />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div role="group" aria-label={heading} className={classes.tickGroup}>
          <Text size={TextSize.ms} weight={TextWeight.SemiBold}>
            {heading}
          </Text>
          {options.map((option) => (
            <Checkbox
              key={option.id}
              label={option.label}
              checked={draft.ticks.includes(option.id)}
              onChange={() =>
                onChange((current) => toggleKpiTick(current, option.id))
              }
            />
          ))}
        </div>
      )}
      {tickHint && (
        <Text size={TextSize.s} highlight={TextHighlight.Background}>
          {tickHint}
        </Text>
      )}
    </div>
  )
}

interface BaseFieldProps<D extends KpiMeasureDraft> {
  subject: 'question' | 'row'
  draft: D
  onChange: (change: (current: D) => D) => void
}

export const KpiBaseField = <D extends KpiMeasureDraft>({
  subject,
  draft,
  onChange
}: BaseFieldProps<D>) => (
  <RadioGroup
    caption="Out of"
    name="kpi-base"
    value={draft.base}
    onChange={(_, value) =>
      onChange((current) => setKpiDraftBase(current, value as KpiBase))
    }
    items={[
      {
        value: KpiBase.Answered,
        label: `Everyone who answered the ${subject}`
      },
      {
        value: KpiBase.AllCompletes,
        label: 'Everyone who completed the survey'
      }
    ]}
  />
)
